import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Sparkles, Coins } from "lucide-react";
import { toast } from "sonner";
import { http } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { UpgradeDialog } from "../components/UpgradeDialog";
import { LeadCard } from "../components/leads/LeadCard";
import { LeadSearchForm } from "../components/leads/LeadSearchForm";
import { useLeads, leadPrefill } from "../hooks/useLeads";
import { LEAD_PACKS as PACKS } from "../lib/pricing";

const BuyPacks = () => {
  const [buying, setBuying] = useState("");
  const buy = async (lookup_key) => {
    setBuying(lookup_key);
    try {
      const { data } = await http.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Could not start checkout.");
      setBuying("");
    }
  };
  return (
    <div className="mt-6 rounded-2xl border border-[#E2DCD2] bg-white p-6 shadow-sm">
      <div className="flex items-center gap-2 text-[#1B3A4B] font-semibold"><Sparkles className="w-4 h-4 text-[#A6844A]" /> Buy lead coins</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
        {PACKS.map((p) => (
          <div key={p.lookup} data-testid={`lead-pack-${p.lookup}`} className="rounded-xl border border-[#E2DCD2] p-4 flex flex-col">
            <div className="font-semibold text-[#1B3A4B]">{p.label}</div>
            <div className="font-serif text-2xl font-bold text-[#1C1915] mt-1">{p.price}</div>
            <div className="text-xs text-[#5C564E] mt-1">{p.sub}</div>
            <Button data-testid={`buy-${p.lookup}`} onClick={() => buy(p.lookup)} disabled={!!buying} className="mt-3 rounded-full bg-[#1B3A4B] hover:bg-[#132A37] text-white h-9 text-sm">
              {buying === p.lookup ? <Loader2 className="w-4 h-4 animate-spin" /> : "Buy"}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

const Redeem = ({ L }) => (
  <div className="mt-6 rounded-2xl border border-[#E2DCD2] bg-white p-6 shadow-sm">
    <div className="flex items-center gap-2 text-[#1B3A4B] font-semibold"><Coins className="w-4 h-4 text-[#A6844A]" /> Have a lead code?</div>
    <p className="text-xs text-[#5C564E] mt-1">Enter your code to instantly add lead coins to your balance.</p>
    <div className="flex gap-2 mt-3 max-w-md">
      <Input data-testid="lead-code-input" placeholder="Enter lead code" value={L.leadCode} onChange={(e) => L.setLeadCode(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") L.redeemLeadCode(); }} />
      <Button data-testid="lead-code-button" onClick={L.redeemLeadCode} disabled={L.redeeming || !L.leadCode.trim()} className="rounded-full bg-[#A6844A] hover:bg-[#8f6f3b] text-white shrink-0 px-5">
        {L.redeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : "Redeem"}
      </Button>
    </div>
  </div>
);

const Grid = ({ title, leads, L, onGenerate }) =>
  leads && leads.length > 0 ? (
    <div className="mt-8">
      <h2 className="font-serif text-2xl text-[#1B3A4B] mb-4">{title}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {leads.map((l) => <LeadCard key={l.id} lead={l} onGenerate={onGenerate} onDelete={L.removeLead} now={L.now} />)}
      </div>
    </div>
  ) : null;

export default function LeadsPage() {
  const { refreshCredits } = useAuth();
  const navigate = useNavigate();
  const L = useLeads();
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const generate = (lead, amount) => {
    localStorage.setItem("bidmark_lead_prefill", JSON.stringify(leadPrefill(lead, amount)));
    toast.success("Lead sent to the estimate builder.");
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header onUpgrade={() => setUpgradeOpen(true)} />
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-serif text-4xl text-[#1B3A4B]">Live Leads</h1>
            <p className="text-[#5C564E] mt-1 max-w-xl">
              On-demand, pay-as-you-go leads. One coin runs a live web search and returns real
              prospects — people, businesses, and public project/RFP postings — each with a source
              link you can verify. Coins never expire and are separate from your quote plan.
            </p>
          </div>
          <div data-testid="lead-balance" className="rounded-full bg-[#1B3A4B] text-white px-4 py-2 text-sm inline-flex items-center gap-2">
            <Coins className="w-4 h-4 text-[#C5A059]" />
            {L.trialAvailable ? "1 free trial lead available" : `${L.leadCoins} lead coin${L.leadCoins === 1 ? "" : "s"}`}
          </div>
        </div>
        <div className="rounded-2xl border border-[#E2DCD2] bg-white p-6 shadow-sm">
          <LeadSearchForm L={L} />
        </div>
        <BuyPacks />
        <Redeem L={L} />
        <Grid title="Your latest leads" leads={L.batch} L={L} onGenerate={generate} />
        <Grid title="All saved leads" leads={L.saved} L={L} onGenerate={generate} />
      </main>
      <Footer />
      <UpgradeDialog open={upgradeOpen} onOpenChange={(v) => { setUpgradeOpen(v); if (!v) refreshCredits(); }} />
    </div>
  );
}
